import { FC } from 'react';
import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { PropertyFormData, PropertyStatus, PROPERTY_STATUS_LABELS, AddressSuggestion } from '@/types/index';
import { Input } from '@components/common/Input';
import { Select } from '@components/common/Select';
import { Counter } from '@components/common/Counter';
import { RadioGroup } from '@components/common/RadioGroup';
import { AddressAutocomplete } from '@components/common/AddressAutocomplete';
import { addressService } from '@services/address.service';

interface Step1GeneralProps {
  register: UseFormRegister<PropertyFormData>;
  errors: FieldErrors<PropertyFormData>;
  setValue: UseFormSetValue<PropertyFormData>;
  watch: UseFormWatch<PropertyFormData>;
}

export const Step1General: FC<Step1GeneralProps> = ({
  register,
  errors,
  setValue,
  watch,
}) => {
  // Watch values for Counter and RadioGroup components
  const floorsInBuilding = watch('floorsInBuilding') || 1;
  const rooms = watch('rooms') || 1;
  const bathrooms = watch('bathrooms') || 0;
  const hasGuestToilet = watch('hasGuestToilet') || false;
  const hasElevator = watch('hasElevator') || false;

  // Handle address selection from autocomplete
  const handleAddressSelect = async (suggestion: AddressSuggestion) => {
    // Auto-fill address fields
    setValue('address', suggestion.address);
    setValue('postalCode', suggestion.postalCode);
    setValue('city', suggestion.city);
    setValue('latitude', suggestion.latitude);
    setValue('longitude', suggestion.longitude);
    setValue('egid', suggestion.egid);

    // Fetch building data by EGID
    try {
      const buildingData = await addressService.getBuildingData(suggestion.egid);

      // Auto-fill building fields if available
      if (buildingData.buildingYear) {
        setValue('buildingYear', buildingData.buildingYear);
      }
      if (buildingData.heatingType) {
        setValue('heatingType', buildingData.heatingType);
      }
      if (buildingData.floorsInBuilding) {
        setValue('floorsInBuilding', buildingData.floorsInBuilding);
      }
      if (buildingData.buildingCondition) {
        setValue('buildingCondition', buildingData.buildingCondition);
      }
    } catch (error) {
      console.error('Error fetching building data:', error);
      // Continue without building data (user can fill manually)
    }
  };

  // Generate floor options based on number of floors in building
  const generateFloorOptions = () => {
    const options = [
      { value: 'basement', label: 'Sous-sol' },
      { value: 'ground', label: 'RdC' },
    ];

    for (let i = 2; i <= floorsInBuilding; i++) {
      options.push({ value: `floor_${i}`, label: `Rg${i}` });
    }

    return options;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Général
      </h2>

      {/* Section Statut */}
      <div className="space-y-4">
        <Select
          label="Statut"
          {...register('status', { required: 'Le statut est requis' })}
          options={Object.values(PropertyStatus).map(status => ({
            value: status,
            label: PROPERTY_STATUS_LABELS[status],
          }))}
          error={errors.status?.message}
          required
        />
      </div>

      {/* Section Localisation */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          Localisation
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Adresse <span className="text-red-500">*</span>
          </label>
          <AddressAutocomplete
            value={watch('address') || ''}
            onChange={(value) => setValue('address', value)}
            onSelect={handleAddressSelect}
            error={errors.address?.message}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Code postal"
            {...register('postalCode', { required: 'Le code postal est requis' })}
            error={errors.postalCode?.message}
            required
          />

          <Input
            label="Ville"
            {...register('city', { required: 'La ville est requise' })}
            error={errors.city?.message}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="EGID"
            {...register('egid', { valueAsNumber: true })}
            placeholder="Rempli automatiquement"
            readOnly
            className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
          />
        </div>
      </div>

      {/* Section Stratégie et Affectation */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Stratégie"
            {...register('strategy', { required: 'La stratégie est requise' })}
            options={[
              { value: 'own_use', label: 'Utilisation propre' },
              { value: 'rental', label: 'Rendement' },
              { value: 'mixed', label: 'Mixte' },
            ]}
            error={errors.strategy?.message}
            required
          />

          <Select
            label="Affectation"
            {...register('affectation', { required: 'L\'affectation est requise' })}
            options={[
              { value: 'residential', label: 'Résidentielle' },
              { value: 'commercial', label: 'Commerciale' },
            ]}
            error={errors.affectation?.message}
            required
          />
        </div>
      </div>

      {/* Section Caractéristiques du bâtiment */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          Caractéristiques du bâtiment
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Counter
            label="Nombre de niveaux dans l'immeuble"
            value={floorsInBuilding}
            onChange={(value) => setValue('floorsInBuilding', value)}
            min={1}
            max={50}
            error={errors.floorsInBuilding?.message}
            required
          />

          <Select
            label="Étage"
            {...register('floor', { required: 'L\'étage est requis' })}
            options={generateFloorOptions()}
            error={errors.floor?.message}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Counter
            label="Nombre de pièces"
            value={rooms}
            onChange={(value) => setValue('rooms', value)}
            min={0.5}
            max={20}
            step={0.5}
            error={errors.rooms?.message}
            required
          />

          <Counter
            label="Nombre de salles de bain"
            value={bathrooms}
            onChange={(value) => setValue('bathrooms', value)}
            min={0}
            max={10}
            error={errors.bathrooms?.message}
            required
          />
        </div>
      </div>

      {/* Section Équipements */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          Équipements
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <RadioGroup
            label="WC visiteur"
            name="hasGuestToilet"
            options={[
              { value: 'false', label: 'Non' },
              { value: 'true', label: 'Oui' },
            ]}
            value={String(hasGuestToilet)}
            onChange={(value) => setValue('hasGuestToilet', value === 'true')}
            layout="horizontal"
          />

          <RadioGroup
            label="Ascenseur"
            name="hasElevator"
            options={[
              { value: 'false', label: 'Non' },
              { value: 'true', label: 'Oui' },
            ]}
            value={String(hasElevator)}
            onChange={(value) => setValue('hasElevator', value === 'true')}
            layout="horizontal"
          />
        </div>
      </div>
    </div>
  );
};
